import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NewsService } from '../../news-service.service';
import { ConnectorService } from '../../connector.service';
import { PreferencesService, NewsTickerSpeed } from '../../services/preferences.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-news-ticker',
  templateUrl: './news-ticker.component.html',
  styleUrls: ['./news-ticker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NewsTickerComponent implements OnInit, OnDestroy {
  @ViewChild('banner') banner?: ElementRef<HTMLElement>;

  news: string[] = [];
  activeIndex = 0;
  exitIndex = -1;
  isPaused = false;

  private intervalId: any = null;
  private intervalMs = 5500;
  private touchStartX = 0;
  private newsSub?: Subscription;
  private prefsSub?: Subscription;

  // Progress animation via rAF
  private progressStart = 0;
  private rafId = 0;

  constructor(
    private newsService: NewsService,
    private connectorService: ConnectorService,
    private preferencesService: PreferencesService,
  ) {}

  ngOnInit() {
    this.news = this.newsService.getLatestNews();
    this.startAutoRotate();

    this.newsSub = this.newsService.getNews$().subscribe(news => {
      this.news = news;
      this.activeIndex = 0;
      this.exitIndex = -1;
      this.restartAutoRotate();
    });

    this.prefsSub = this.preferencesService.getPreferences().subscribe(() => {
      this.restartAutoRotate();
    });
  }

  ngOnDestroy() {
    this.stopAutoRotate();
    this.stopProgress();
    this.newsSub?.unsubscribe();
    this.prefsSub?.unsubscribe();
  }

  private getIntervalMs(): number {
    const speed = this.preferencesService.getNewsTickerSpeed();
    switch (speed) {
      case NewsTickerSpeed.SLOW:   return 8000;
      case NewsTickerSpeed.FAST:   return 3500;
      case NewsTickerSpeed.NORMAL:
      default:                     return 5500;
    }
  }

  private startAutoRotate() {
    this.stopAutoRotate();
    this.intervalMs = this.getIntervalMs();
    this.startProgress();
    if (this.news.length <= 1) return;
    this.intervalId = setInterval(() => {
      if (!this.isPaused) this.next();
    }, this.intervalMs);
  }

  private stopAutoRotate() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private restartAutoRotate() {
    this.startAutoRotate();
  }

  // --- Progress : anime background-size via requestAnimationFrame ---

  private startProgress() {
    this.stopProgress();
    if (this.news.length <= 1) {
      this.setProgress(0);
      return;
    }
    this.progressStart = performance.now();
    this.tickProgress();
  }

  private stopProgress() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private tickProgress = () => {
    const elapsed = performance.now() - this.progressStart;
    const pct = Math.min((elapsed / this.intervalMs) * 100, 100);
    this.setProgress(pct);
    if (pct < 100) {
      this.rafId = requestAnimationFrame(this.tickProgress);
    }
  };

  private setProgress(pct: number) {
    const el = this.banner?.nativeElement;
    if (el) {
      el.style.setProperty('--progress', `${pct}%`);
    }
  }

  next() {
    if (this.news.length <= 1) return;
    this.exitIndex = this.activeIndex;
    this.activeIndex = (this.activeIndex + 1) % this.news.length;
    this.startProgress();
    setTimeout(() => { this.exitIndex = -1; }, 500);
  }

  prev() {
    if (this.news.length <= 1) return;
    this.exitIndex = this.activeIndex;
    this.activeIndex = (this.activeIndex - 1 + this.news.length) % this.news.length;
    this.startProgress();
    setTimeout(() => { this.exitIndex = -1; }, 500);
  }

  goTo(index: number) {
    if (index === this.activeIndex) return;
    this.exitIndex = this.activeIndex;
    this.activeIndex = index;
    this.restartAutoRotate();
    setTimeout(() => { this.exitIndex = -1; }, 500);
  }

  pause() { this.isPaused = true; }
  resume() { this.isPaused = false; }

  onTouchStart(e: TouchEvent) {
    this.touchStartX = e.touches[0].clientX;
  }

  onTouchEnd(e: TouchEvent) {
    const diff = this.touchStartX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) { this.next(); } else { this.prev(); }
      this.restartAutoRotate();
    }
  }

  isConnected(): boolean {
    return this.connectorService.isConnected();
  }
}
